import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { X, Copy, ExternalLink, Ban, Loader2, User2 } from 'lucide-react';
import { salesApi, formatMoney, serverError } from '../../lib/salesApi';

const CY = '#90E0EF';
const STATUS_TONE = {
  paid: { c: '#7ed4a0', b: 'rgba(126,212,160,0.4)', bg: 'rgba(126,212,160,0.08)' },
  pending: { c: '#9a9a9a', b: '#333', bg: 'transparent' },
  checkout_open: { c: '#90E0EF', b: 'rgba(144,224,239,0.45)', bg: 'rgba(144,224,239,0.08)' },
  cancelled: { c: '#e88ba0', b: 'rgba(232,139,160,0.4)', bg: 'rgba(232,139,160,0.07)' },
  expired: { c: '#e4c07a', b: 'rgba(228,192,122,0.45)', bg: 'rgba(228,192,122,0.08)' },
  refunded: { c: '#c89dff', b: 'rgba(200,157,255,0.42)', bg: 'rgba(200,157,255,0.08)' },
  payment_conflict: { c: '#e4c07a', b: 'rgba(228,192,122,0.5)', bg: 'rgba(228,192,122,0.1)' },
};

export function StatusPill({ status, className = '' }) {
  const t = STATUS_TONE[status] || STATUS_TONE.pending;
  return (
    <span className={`inline-flex items-center text-[10px] font-mono px-2 py-0.5 border uppercase tracking-[0.12em] ${className}`}
      style={{ color: t.c, borderColor: t.b, background: t.bg }}>
      {(status || 'pending').replace('_', ' ')}
    </span>
  );
}

function DRow({ k, v, mono = true }) {
  if (v == null || v === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-[#141414]">
      <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#666] shrink-0">{k}</span>
      <span className={`text-right break-all ${mono ? 'font-mono text-xs' : 'text-sm'} text-[#e0e0e0]`}>{v}</span>
    </div>
  );
}

export default function SaleDetailModal({ saleId, open, onClose, onChanged, onViewClient }) {
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !saleId) return;
    setLoading(true);
    salesApi.get(saleId).then(setSale).catch((e) => toast.error(serverError(e))).finally(() => setLoading(false));
  }, [open, saleId]);

  if (!open) return null;
  const paymentUrl = sale ? `${window.location.origin}/p/${sale.public_code}` : '';

  async function cancel() {
    setBusy(true);
    try {
      await salesApi.cancel(saleId);
      toast.success('Sale cancelled.');
      const fresh = await salesApi.get(saleId);
      setSale(fresh);
      onChanged?.();
    } catch (e) { toast.error(serverError(e)); } finally { setBusy(false); }
  }

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex justify-end admin-root" onClick={onClose} data-testid="sale-detail-modal">
        <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
          transition={{ type: 'tween', duration: 0.2 }} onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md h-full bg-[#080808] border-l border-[#1a1a1a] overflow-y-auto">
          <div className="sticky top-0 flex items-center justify-between px-6 h-14 border-b border-[#161616] bg-[#080808]">
            <span className="font-outfit font-semibold text-white">Transaction</span>
            <button onClick={onClose} style={{ cursor: 'pointer' }} className="text-[#888] hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          {loading || !sale ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-5 h-5 animate-spin" style={{ color: CY }} /></div>
          ) : (
            <div className="p-6">
              <div className="flex items-center justify-between">
                <StatusPill status={sale.status} />
                <span className="font-mono tracking-[0.28em] text-lg" style={{ color: CY }}>{sale.public_code}</span>
              </div>
              <h2 className="font-outfit font-bold text-white text-2xl tracking-tight mt-4">{sale.item_title}</h2>
              {sale.edition && <p className="text-sm font-mono text-[#888] mt-0.5">{sale.edition}</p>}
              <p className="font-outfit font-bold text-white mt-4" style={{ fontSize: '2.2rem', lineHeight: 1 }}>{formatMoney(sale.amount_minor, sale.currency)}</p>

              {sale.cards && sale.cards.length > 0 && (
                <div className="mt-5" data-testid="detail-card-list">
                  <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#666] mb-2">{sale.cards.length} Card Edition{sale.cards.length === 1 ? '' : 's'}</p>
                  <div className="flex flex-wrap gap-2">
                    {sale.cards.map((c) => (
                      <span key={c} className="font-mono text-xs px-2 py-1 border" style={{ color: CY, borderColor: 'rgba(144,224,239,0.35)' }}>#{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {sale.status === 'payment_conflict' && (
                <div className="mt-5 border px-4 py-4" data-testid="detail-conflict-panel" style={{ borderColor: 'rgba(228,192,122,0.4)', background: 'rgba(228,192,122,0.06)' }}>
                  <p className="text-[10px] font-mono uppercase tracking-[0.16em] mb-2" style={{ color: '#e4c07a' }}>Payment conflict — manual action needed</p>
                  <p className="text-xs font-mono text-[#d8c79a] leading-relaxed">{sale.conflict_reason || 'One or more cards were already sold when this payment completed.'}</p>
                  <p className="text-[11px] font-mono text-[#888] mt-2">The buyer was charged. Reassign available cards or refund via Stripe.</p>
                </div>
              )}

              <div className="mt-6">
                <DRow k="Seller" v={sale.seller_label} />
                <DRow k="Artist" v={sale.artist_name} />
                {sale.card_number_display && <DRow k="Card" v={`#${sale.card_number_display}`} />}
                <DRow k="Client" v={sale.buyer_name} />
                <DRow k="Email" v={sale.buyer_email} />
                <DRow k="Created" v={sale.created_at ? new Date(sale.created_at).toLocaleString() : null} />
                <DRow k="Checkout started" v={sale.checkout_started_at ? new Date(sale.checkout_started_at).toLocaleString() : null} />
                <DRow k="Paid" v={sale.paid_at ? new Date(sale.paid_at).toLocaleString() : null} />
                <DRow k="Sale ID" v={sale.id} />
                <DRow k="Stripe session" v={sale.stripe_checkout_session_id} />
                <DRow k="Payment intent" v={sale.stripe_payment_intent_id} />
                {sale.notes && <DRow k="Notes" v={sale.notes} />}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <button onClick={() => { navigator.clipboard.writeText(paymentUrl); toast.success('Link copied'); }} style={{ cursor: 'pointer' }}
                  data-testid="detail-copy-link" className="inline-flex items-center gap-2 border border-[#2a2a2a] text-[#ccc] hover:text-white hover:border-[#90E0EF]/50 px-3 py-2 text-[10px] font-mono uppercase tracking-[0.14em]">
                  <Copy className="w-3 h-3" /> Copy Link
                </button>
                <a href={paymentUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#2a2a2a] text-[#ccc] hover:text-white hover:border-[#90E0EF]/50 px-3 py-2 text-[10px] font-mono uppercase tracking-[0.14em]">
                  <ExternalLink className="w-3 h-3" /> Open Payment Page
                </a>
                {sale.client_id && (
                  <button onClick={() => onViewClient?.(sale.client_id)} style={{ cursor: 'pointer' }}
                    className="inline-flex items-center gap-2 border border-[#2a2a2a] text-[#ccc] hover:text-white hover:border-[#90E0EF]/50 px-3 py-2 text-[10px] font-mono uppercase tracking-[0.14em]">
                    <User2 className="w-3 h-3" /> View Client
                  </button>
                )}
                {sale.status !== 'paid' && sale.status !== 'cancelled' && (
                  <button onClick={cancel} disabled={busy} style={{ cursor: 'pointer' }}
                    data-testid="detail-cancel-sale" className="inline-flex items-center gap-2 border border-[#3a2020] text-[#e88ba0] hover:bg-[#2a1515] px-3 py-2 text-[10px] font-mono uppercase tracking-[0.14em] disabled:opacity-50">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />} Cancel Sale
                  </button>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
